import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./addon-fees-new.css";

export default function AddonFeeList() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin")).school;

  const [fees, setFees] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [view, setView] = useState("list");

  const [filter, setFilter] = useState({
    class_id: "",
    section_id: "",
    student_id: "",
    title: "",
  });

  /* ================= LOAD MASTER DATA ================= */

  useEffect(() => {
    API.get("/school-admin/classes/", {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => setClasses(res.data.classes || []));
  }, [school.id]);

  useEffect(() => {
    if (!filter.class_id) {
      setSections([]);
      setStudents([]);
      return;
    }

    API.get(`/school-admin/sections/?class_id=${filter.class_id}`, {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => setSections(res.data.sections || []));
  }, [filter.class_id, school.id]);

  useEffect(() => {
    if (!filter.class_id || !filter.section_id) {
      setStudents([]);
      return;
    }

    API.get(
      `/school-admin/students/?class_id=${filter.class_id}&section_id=${filter.section_id}`,
      { headers: { "X-SCHOOL-ID": school.id } }
    ).then((res) => setStudents(res.data.students || []));
  }, [filter.class_id, filter.section_id, school.id]);

  /* ================= LOAD FEES ================= */

  const loadFees = async () => {
    const params = {};
    if (filter.class_id) params.class_id = filter.class_id;
    if (filter.section_id) params.section_id = filter.section_id;
    if (filter.student_id) params.student_id = filter.student_id;
    if (filter.title) params.title = filter.title;

    const query = new URLSearchParams(params).toString();

    const res = await API.get(
      `/school-admin/addon-fees/${query ? `?${query}` : ""}`,
      { headers: { "X-SCHOOL-ID": school.id } }
    );

    setFees(res.data.addon_fees || []);
  };

  useEffect(() => {
    loadFees();
  }, [
    filter.class_id,
    filter.section_id,
    filter.student_id,
    filter.title,
  ]);

  /* ================= STATUS TOGGLE ================= */

  const toggleStatus = async (fee) => {
    const newStatus = fee.status === "paid" ? "pending" : "paid";

    await API.post(
      `/school-admin/addon-fees/${fee.id}/mark-status/`,
      { status: newStatus },
      { headers: { "X-SCHOOL-ID": school.id } }
    );

    loadFees();
  };

  /* ================= DELETE ================= */

  const deleteFee = async (id) => {
    if (!window.confirm("Delete this addon fee?")) return;

    await API.delete(`/school-admin/addon-fees/${id}/delete/`, {
      headers: { "X-SCHOOL-ID": school.id },
    });

    loadFees();
  };

  /* ================= UI ================= */

  return (
    <div className="addon-wrapper">
      <h2 className="addon-title">Addon Fees</h2>

      {/* ================= FILTER BAR ================= */}
      <div className="addon-filters">
        <select
          value={filter.class_id}
          onChange={(e) =>
            setFilter({
              class_id: e.target.value,
              section_id: "",
              student_id: "",
              title: filter.title,
            })
          }
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          value={filter.section_id}
          disabled={!sections.length}
          onChange={(e) =>
            setFilter({
              ...filter,
              section_id: e.target.value,
              student_id: "",
            })
          }
        >
          <option value="">All Sections</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          value={filter.student_id}
          disabled={!students.length}
          onChange={(e) =>
            setFilter({ ...filter, student_id: e.target.value })
          }
        >
          <option value="">All Students</option>
          {students.map((st) => (
            <option key={st.id} value={st.id}>
              {st.name}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Filter by title"
          value={filter.title}
          onChange={(e) =>
            setFilter({ ...filter, title: e.target.value })
          }
        />

        <div className="view-toggle">
          <button
            className={view === "list" ? "active" : ""}
            onClick={() => setView("list")}
          >
            📋 List
          </button>
          <button
            className={view === "card" ? "active" : ""}
            onClick={() => setView("card")}
          >
            🧱 Cards
          </button>
        </div>
      </div>

      {/* ================= LIST VIEW ================= */}
      {view === "list" && (
        <table className="addon-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>Class</th>
              <th>Section</th>
              <th>Title</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {fees.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty">
                  No addon fees found
                </td>
              </tr>
            ) : (
              fees.map((f) => (
                <tr key={f.id}>
                  <td>{f.student_name}</td>
                  <td>{f.class_name}</td>
                  <td>{f.section_name}</td>
                  <td>{f.title}</td>
                  <td>₹ {f.amount}</td>

                  <td>
                    <span
                      className={`status ${
                        f.status === "paid" ? "paid" : "pending"
                      }`}
                    >
                      {f.status.toUpperCase()}
                    </span>
                  </td>

                  <td>
                    <button
                      className={`btn small ${
                        f.status === "paid"
                          ? "btn-warning"
                          : "btn-success"
                      }`}
                      onClick={() => toggleStatus(f)}
                    >
                      {f.status === "paid"
                        ? "Mark Pending"
                        : "Mark Paid"}
                    </button>

                    <button
                      className="btn-danger small"
                      onClick={() => deleteFee(f.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* ================= CARD VIEW ================= */}
      {view === "card" && (
        <div className="addon-card-grid">
          {fees.map((f) => (
            <div key={f.id} className="addon-card">
              <div className="status-badge">
                {f.status === "paid" ? "PAID" : "PENDING"}
              </div>

              <div><b>Student:</b> {f.student_name}</div>
              <div><b>Class:</b> {f.class_name}</div>
              <div><b>Section:</b> {f.section_name}</div>
              <div><b>Title:</b> {f.title}</div>

              <div className="amount">₹ {f.amount}</div>

              <button
                className={
                  f.status === "paid" ? "btn-warning" : "btn-success"
                }
                onClick={() => toggleStatus(f)}
              >
                {f.status === "paid" ? "Mark Pending" : "Mark Paid"}
              </button>

              <button
                className="btn-danger"
                onClick={() => deleteFee(f.id)}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}